import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One-shot CSS "pop" trigger for like/favourite taps.
 *
 * The Lottie reaction overlays silently no-op when the dotlottie WASM chunk
 * fails to load (their loadError handler just clears state), so icons play
 * this short scale pop as the always-available animation (keyframe
 * `like-pop` in styles/mixins/animations.less). No-ops under
 * prefers-reduced-motion, matching the Lottie triggers.
 */
export function usePopAnimation(durationMs = 400): [boolean, () => void] {
  const [popping, setPopping] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const trigger = useCallback(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(timerRef.current);
    setPopping(true);
    timerRef.current = setTimeout(() => setPopping(false), durationMs);
  }, [durationMs]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return [popping, trigger];
}
